<template>
  <div class="p-6">
    <div v-if="loading" class="text-center text-gray-500">
      Loading profile...
    </div>

    <div v-else class="grid grid-cols-1 md:grid-cols-3 gap-8">
      <!-- Left Column: User Details -->
      <div class="md:col-span-1">
        <div class="bg-white p-6 rounded-lg shadow-md">
          <h2 class="text-xl font-bold text-gray-800 border-b pb-4 mb-4">Profile Details</h2>
          <div class="space-y-4">
            <div>
              <label class="block text-sm font-medium text-gray-500">Username</label>
              <p class="text-lg text-gray-900">{{ user.username }}</p>
            </div>
            <div>
              <label class="block text-sm font-medium text-gray-500">Email</label>
              <p class="text-lg text-gray-900">{{ user.email }}</p>
            </div>
          </div>
        </div>
      </div>

      <!-- Right Column: Balance and Payment -->
      <div class="md:col-span-2">
        <div class="bg-white p-6 rounded-lg shadow-md">
          <h2 class="text-xl font-bold text-gray-800 border-b pb-4 mb-4">Credits & Billing</h2>

          <!-- Current Balance -->
          <div class="mb-8">
            <label class="block text-sm font-medium text-gray-500">Current Balance</label>
            <p class="text-4xl font-bold text-blue-600">{{ formatCurrency(user.credits) }}</p>
          </div>

          <!-- Add Credits Section -->
          <div>
            <h3 class="text-lg font-semibold text-gray-700">Add More Credits</h3>
            <p class="text-sm text-gray-600 mt-1 mb-4">
              Top up your balance to continue grading. Approximately <span class="font-bold">200 pages</span> can be processed for every <span class="font-bold">$10</span>.
            </p>

            <!-- Predefined Amount Buttons -->
            <div class="flex space-x-2 mb-4">
              <button
                  v-for="amount in predefinedAmounts"
                  :key="amount"
                  @click="setAmount(amount)"
                  class="px-4 py-2 rounded-md border transition-colors"
                  :class="amountToAdd === amount ? 'bg-blue-600 text-white border-blue-600' : 'bg-gray-100 hover:bg-gray-200 border-gray-300'"
              >
                {{ formatCurrency(amount) }}
              </button>
            </div>

            <!-- Custom Amount Input -->
            <div class="flex items-center space-x-4">
              <input
                  v-model.number="amountToAdd"
                  type="number"
                  min="5"
                  step="1"
                  placeholder="Or enter custom amount (min $5)"
                  class="border p-2 rounded w-full md:w-1/2"
              >
              <button
                  @click="startPaymentProcess"
                  class="bg-green-500 text-white px-6 py-2 rounded-md hover:bg-green-600 transition disabled:opacity-50"
                  :disabled="isPaymentProcessing || amountToAdd < 5"
              >
                <span v-if="isPaymentProcessing">Processing...</span>
                <span v-else>Buy Credits</span>
              </button>
            </div>
            <p v-if="amountToAdd > 0 && amountToAdd < 5" class="text-red-500 text-xs mt-1">
              Minimum purchase amount is $5.00.
            </p>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import axios from '../services/axiosInstance'
import { loadStripe } from '@stripe/stripe-js'
import { useModalStore } from '@/stores/modal'

const user = ref({
  username: '',
  email: '',
  credits: 0.00
})
const loading = ref(true)
const amountToAdd = ref(10) // Default to a common amount
const isPaymentProcessing = ref(false)
const modal = useModalStore()

const predefinedAmounts = [10, 20, 50]

const fetchUserData = async () => {
  try {
    const response = await axios.get('/user')
    user.value = response.data
  } catch (error) {
    console.error('Error fetching user data:', error)
  } finally {
    loading.value = false
  }
}

const formatCurrency = (value) => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD'
  }).format(value || 0)
}

const setAmount = (amount) => {
  amountToAdd.value = amount
}

const startPaymentProcess = async () => {
  if (amountToAdd.value < 5) {
    await modal.alert('The minimum purchase amount is $5.00.') // REPLACE alert
    return
  }

  isPaymentProcessing.value = true
  try {
    const response = await axios.post('/payments/create-checkout-session', {
      // Stripe expects the amount in cents
      amount: amountToAdd.value * 100
    })
    const sessionId = response.data.id

    // Replace with your actual Stripe publishable key
    const stripePublicKey = 'pk_live_51PxirxRqMRloJu3MDrALeUPgmSYTWDpnLHIUl7L6yuP3r4V4UvF0lwkoyVDYxzjoyWAW9EjxKXK5sYv61HwTENHL00jKw936Jx'
    const stripe = await loadStripe(stripePublicKey)

    await stripe.redirectToCheckout({ sessionId })
  } catch (error) {
    console.error('Error starting payment process:', error)
    alert('Could not initiate payment. Please try again.')
  } finally {
    isPaymentProcessing.value = false
  }
}

onMounted(fetchUserData)
</script>

<style scoped>
/* Scoped styles can be added here if needed, but Tailwind handles most of it. */
</style>
